import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ScrollReveal from "scrollreveal";
import { HiOutlineChatBubbleLeftRight, HiOutlinePencilSquare, HiOutlineSparkles, HiOutlineClipboardDocumentCheck } from "react-icons/hi2";
import { GoCheckCircleFill } from "react-icons/go";
import styles from "./Services.module.css";

import img1 from "../../images/לילך מורד גן יבנה 42.webp";
import img2 from "../../images/לילך מורד גן יבנה 55.webp";
import img3 from "../../images/לילך מורד גן יבנה 122.webp";
import img4 from "../../images/לילך מורד גן יבנה 98.webp";

import hero1 from "../../images/לילך מורד גן יבנה 12.webp";
import hero2 from "../../images/לילך מורד גן יבנה 35.webp";
import hero3 from "../../images/לילך מורד גן יבנה 70.webp";
import hero4 from "../../images/לילך מורד גן יבנה 100.webp";

import extra1a from "../../images/לילך מורד גן יבנה 7.webp";
import extra1b from "../../images/לילך מורד גן יבנה 15.webp";
import extra2a from "../../images/לילך מורד גן יבנה 27.webp";
import extra2b from "../../images/לילך מורד גן יבנה 33.webp";
import extra3a from "../../images/לילך מורד גן יבנה 65.webp";
import extra3b from "../../images/לילך מורד גן יבנה 68.webp";
import extra4a from "../../images/לילך מורד גן יבנה 95.webp";
import extra4b from "../../images/לילך מורד גן יבנה 92.webp";

gsap.registerPlugin(ScrollTrigger);

const WHATSAPP_NUMBER = "972505864422";
const waUrl = (t) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`היי לילך, הגעתי מהאתר שלך ואשמח לשמוע עוד על ${t}`)}`;

const services = [
  {
    id: 1, number: "01", title: "ליווי עיצובי מלא", subtitle: "FULL DESIGN",
    brief: "מהחלום ועד המפתח - ליווי צמוד שמוציא אתכם מהבלאגן ומכניס לבית",
    image: img1, gallery: [hero1, img1, extra1a, extra1b],
    description: "ליווי עיצובי מלא הוא הדרך לעבור את כל התהליך בראש שקט ובלי הפתעות. מאפיון הצרכים שלכם, דרך תכנון מדויק והדמיות ריאליסטיות, ועד פיקוח צמוד בשטח - כל החלטה מחושבת מראש, כל שקל הולך למקום הנכון, והתוצאה היא בית שמותאם בדיוק לחיים שלכם.",
    stages: [
      { num: "01", title: "פגישת פרוגרמה", text: "אפיון צרכים מלא ופרטני - מי אתם, מה השגרה שלכם, מה אתם עושים ברגע שאתם מגיעים הביתה. כי בית טוב מתחיל בהבנה של החיים שלכם" },
      { num: "02", title: "תכנון", text: "2-3 סקיצות עם הסבר יתרונות וחסרונות של כל אפשרות. כל פונקציה במקום המחושב שלה - פרקטי, חכם, ומותאם בדיוק לכם" },
      { num: "03", title: "הדמיה", text: "הדמיה ריאליסטית שלא משאירה מקום לספק - תראו את הבית שלכם לפני שמתחילים. עם מקום להערות ושינויים" },
      { num: "04", title: "תוכניות ופיקוח", text: "תוכניות מפורטות לכל בעל מקצוע - אינסטלציה, מיזוג, תאורה, ריצוף, נגרויות. ופיקוח עליון בשטח שמוודא שהכל נעשה לפי התוכנית" },
    ],
    suitableFor: ["אתם בונים בית מאפס ורוצים לעשות את זה פעם אחת - נכון", "אתם לפני שיפוץ מאסיבי ומרגישים שאתם לא יודעים מאיפה להתחיל", "קיבלתם דירת קבלן ואתם לא מוכנים לשלם על טעויות שאפשר למנוע", "יש לכם חלל מסחרי שצריך תוצאה שמוכרת את עצמה", 'אתם יזמים או אנשי נדל"ן שמבינים שעיצוב נכון מעלה ערך'],
    includes: ["אפיון צרכים מלא ומדויק", "2-3 סקיצות תכנון", "הדמיות ריאליסטיות", "לוח השראה, צבעים וחומרים", "תוכניות עבודה לכל בעלי המקצוע", "פיקוח עליון בשטח", "ליווי ממוקד בחנויות וספקים", "תיאום מלא מול קבלנים"],
  },
  {
    id: 2, number: "02", title: "תכנון ועיצוב מטבח", subtitle: "KITCHEN DESIGN",
    brief: "המטבח הוא הלב של הבית - תנו לו את התכנון שהוא מגיע לו",
    image: img2, gallery: [hero2, img2, extra2a, extra2b],
    description: "המטבח הוא הלב של הבית - המקום שבו המשפחה נפגשת, שבו מבשלים, אוכלים ומעבירים את רוב הזמן ביחד. לכן כל פרט בו חייב להיות מותאם בדיוק לכם - לאופי הבישולים שלכם, לגודל המשפחה, לשגרת היומיום ולמה שבאמת חשוב לכם. מאחסון חכם שמנצל כל סנטימטר, דרך בחירת חומרים שמחזיקים לאורך שנים, ועד הדמיה שמראה לכם בדיוק איך הכל ייראה - כל החלטה נעשית עם מחשבה, כדי שתקבלו מטבח שעובד בשבילכם ולא רק נראה טוב בתמונה.",
    stages: [
      { num: "01", title: "פגישה ואפיון", text: "הבנת השטח, אופי השימוש שלכם, התקציב, ומה לא עובד היום. כי מטבח טוב מתחיל בשאלות הנכונות" },
      { num: "02", title: "תכנון מלא", text: "פריסה חכמה בהתאם למצב הקיים או להזזת תשתיות - כל סנטימטר מנוצל" },
      { num: "03", title: "הדמיה ובחירות", text: "הדמיה ריאליסטית של המטבח, בחירת צבעים וחומרים, והצעת ספקים אמינים" },
      { num: "04", title: "ליווי מקצועי", text: "פגישה בחברת המטבחים, ליווי בבחירות, וודאות שהתוצאה תהיה בדיוק כמו בהדמיה" },
    ],
    suitableFor: ["אתם רוצים להחליף מטבח ולא לחזור על טעויות של המטבח הקודם", "אתם משפצים את אזור המטבח ורוצים לנצל כל סנטימטר", "קיבלתם הצעות מחברות מטבחים ואתם לא בטוחים מה נכון", "אתם רוצים מטבח שנראה מדהים אבל גם עובד ביומיום"],
    includes: ["תכנון מלא של המטבח", "הדמיה ריאליסטית", "בחירת צבעים וחומרים", "הצעת ספקים אמינים", "פגישה בחברת מטבחים", "תוכנית עבודה מפורטת"],
  },
  {
    id: 3, number: "03", title: "פגישת ייעוץ", subtitle: "CONSULTATION",
    brief: "כיוון מקצועי שחוסך לכם עשרות אלפי שקלים של טעויות",
    image: img3, gallery: [hero3, img3, extra3a, extra3b],
    description: "פגישת ייעוץ אחת יכולה לחסוך לכם עשרות אלפי שקלים של טעויות. תקבלו תוכניות ברורות, פלטת צבעים שעובדת, תכניות נגרויות לפי הצורך והעמדה נכונה של החלל - הכל ביד, מסודר ומוכן. הקבלן יידע בדיוק מה לעשות ואתם תיכנסו לתהליך עם ביטחון מלא.",
    stages: [
      { num: "01", title: "שיחת היכרות", text: "הבנת הצרכים, המטרות והתקציב שלכם - מה חשוב לכם ומה אפשר לוותר עליו" },
      { num: "02", title: "ביקור בשטח", text: "הכרת החלל, מדידות, הבנת המצב הקיים והפוטנציאל שהוא מסתיר" },
      { num: "03", title: "תכנון והכוונה", text: "תוכנית חלוקה, העמדה נכונה, פלטת צבעים, ותכניות נגרויות לפי הצורך" },
      { num: "04", title: "מסירת חומרים", text: "כל המסמכים והתוכניות ביד - הקבלן יודע בדיוק מה לבצע, ואתם שולטים בתהליך" },
    ],
    suitableFor: ["אתם רוצים לנהל את השיפוץ בעצמכם אבל לא מוכנים להיכנס בלי תכנון", "יש לכם דירת קבלן ואתם מסתכלים על החלל ולא יודעים מאיפה להתחיל", "התקציב שלכם מוגדר ואתם צריכים לדעת איפה להשקיע ועל מה אפשר לוותר", "אתם רוצים למנוע חריגות תקציב עוד לפני שהקבלן נכנס לשטח"],
    includes: ["תוכנית חלוקה מחדש", "העמדה נכונה של החלל", "פלטת צבעים", "תכניות נגרויות לפי הצורך", "תוכניות מפורטות לקבלן", "המלצות על ספקים אמינים"],
  },
  {
    id: 4, number: "04", title: "שירותי אדריכלות מלאים", subtitle: "ARCHITECTURE",
    brief: "אדריכלות ועיצוב פנים תחת קורת גג אחת - בלי פערים, בלי אי הבנות",
    image: img4, gallery: [hero4, img4, extra4a, extra4b],
    description: "אדריכלות ועיצוב פנים תחת קורת גג אחת - זה הדבר שמונע פערים, חוסך זמן ושומר על התקציב. מהגשת ההיתרים ועד בחירת הידית על הדלת - הכל מתואם, מדובר ומתוכנן כך שכל החלטה מבנית ועיצובית עובדת יד ביד, בלי סתירות ובלי הפתעות.",
    stages: [
      { num: "01", title: "בדיקת היתכנות", text: "ניתוח המצב הקיים, הבנת הפוטנציאל, הגדרת כיוון וציפיות ריאליסטיות" },
      { num: "02", title: "תכנון אדריכלי", text: "תוכניות מבנה מפורטות, הגשת היתרי בנייה, וליווי הליכי רישוי מול הרשויות" },
      { num: "03", title: "עיצוב פנים משולב", text: "תכנון פנים שעובד יד ביד עם האדריכלות - כל החלטה מתואמת, בלי סתירות" },
      { num: "04", title: "ליווי וביצוע", text: "הדמיות, תוכניות עבודה מפורטות, ופיקוח צמוד בשטח לאורך כל הדרך" },
    ],
    suitableFor: ["אתם בונים מאפס ורוצים שהתכנון המבני והעיצוב יעבדו ביחד מהיום הראשון", "אתם מתכננים תוספת בנייה ולא רוצים שהמבנה יתנגש עם העיצוב", "יש לכם שינויים מבניים רציניים שדורשים היתרים וליווי מקצועי צמוד", 'אתם יזמים או אנשי נדל"ן שיודעים שתוצאה מדויקת מעלה ערך'],
    includes: ["תכנון אדריכלי מלא", "היתרי בנייה", "עיצוב פנים משולב", "ליווי הליכי רישוי", "הדמיות ריאליסטיות", "פיקוח צמוד בשטח"],
  },
];

const stageIcons = [
  <HiOutlineChatBubbleLeftRight />,
  <HiOutlinePencilSquare />,
  <HiOutlineSparkles />,
  <HiOutlineClipboardDocumentCheck />,
];

const ServicesGrid = () => {
  const [hoveredTile, setHoveredTile] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const scrollProgressRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const header = sectionRef.current?.querySelector("[data-services-header]");
      if (header) {
        gsap.fromTo(header, { opacity: 0, y: 30 }, {
          opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: header, start: "top 85%", once: true },
        });
      }

      gsap.fromTo(`.${styles.gridTile}`,
        { clipPath: "inset(100% 0 0 0)", opacity: 0 },
        { clipPath: "inset(0% 0 0 0)", opacity: 1, stagger: 0.15, duration: 1.2, ease: "power4.out",
          scrollTrigger: { trigger: `.${styles.servicesGrid}`, start: "top 82%" } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  /* ── ScrollReveal modal animations ── */
  useEffect(() => {
    if (!selectedService || !modalRef.current || !overlayRef.current) return;
    const modalEl = modalRef.current;
    const container = overlayRef.current;

    const raf = requestAnimationFrame(() => {
      if (!container || !modalEl) return;

      const sr = ScrollReveal({
        container: container,
        reset: false,
        distance: "28px",
        duration: 800,
        easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        viewFactor: 0.08,
        viewOffset: { top: 0, right: 0, bottom: 40, left: 0 },
      });

      /* Intro elements - staggered entry */
      const badge = modalEl.querySelector("[data-anim='badge']");
      const title = modalEl.querySelector("[data-anim='title']");
      const desc = modalEl.querySelector("[data-anim='desc']");

      if (badge) sr.reveal(badge, { origin: "right", distance: "32px", delay: 80, duration: 700 });
      if (title) sr.reveal(title, { origin: "bottom", distance: "24px", delay: 200, duration: 800 });
      if (desc) sr.reveal(desc, { origin: "bottom", distance: "20px", delay: 320, duration: 750 });

      /* Scroll-revealed elements */
      const revealEls = modalEl.querySelectorAll("[data-reveal]");
      revealEls.forEach((el) => {
        const delayMs = Math.round(parseFloat(el.dataset.revealDelay || "0") * 800);
        const dir = el.dataset.revealDir;
        sr.reveal(el, {
          origin: dir === "right" ? "right" : dir === "left" ? "left" : "bottom",
          distance: dir ? "48px" : "28px",
          delay: delayMs,
        });
      });

      modalEl._sr = sr;
    });

    return () => {
      cancelAnimationFrame(raf);
      if (modalEl?._sr) {
        modalEl._sr.destroy();
        modalEl._sr = null;
      }
    };
  }, [selectedService]);

  const openModal = useCallback((svc) => {
    setSelectedService(svc);
    requestAnimationFrame(() => { if (overlayRef.current) overlayRef.current.scrollTop = 0; });
  }, []);
  const closeModal = useCallback(() => setSelectedService(null), []);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [closeModal]);

  /* Scroll progress bar */
  useEffect(() => {
    if (!selectedService || !overlayRef.current) return;
    const container = overlayRef.current;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const progress = scrollTop / (scrollHeight - clientHeight);
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.transform = `scaleX(${progress})`;
      }
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedService]);

  return (
    <div className={styles.servicesSection} ref={sectionRef} id="services">
      <div className={styles.sectionHeader} data-services-header>
        <h2 className={styles.sectionTitle}>שירותי הסטודיו</h2>
        <p className={styles.sectionSubtitle}>לחצו על כל שירות והוא ייפתח</p>
      </div>
      <div className={styles.servicesGrid} ref={gridRef}>
        {services.map((svc) => (
          <div key={svc.id}
            className={`${styles.gridTile} ${styles[`tile${svc.id}`]} ${hoveredTile === svc.id ? styles.tileHover : ""}`}
            onMouseEnter={() => setHoveredTile(svc.id)} onMouseLeave={() => setHoveredTile(null)}
            onClick={() => openModal(svc)}>
            <img src={svc.image} alt={svc.title} className={styles.tileImg} loading="lazy" style={svc.id === 3 ? { objectPosition: "center 40%" } : undefined} />
            <div className={styles.tileOverlay} />
            <div className={styles.tileOverlay} />
            <div className={styles.tileLabelWrap}>
              <h3 className={styles.tileName}>{svc.title}</h3>
              <p className={styles.tileBrief}>{svc.brief}</p>
              <span className={styles.tileLink}>לפרטים נוספים
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M17 7H7M17 7V17" /></svg>
              </span>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {selectedService && (
          <motion.div className={styles.modalOverlay} ref={overlayRef}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
            onClick={closeModal} onWheel={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={(e) => { e.stopPropagation(); closeModal(); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
            <div className={styles.scrollProgress} ref={scrollProgressRef} />

            <div className={styles.modalPanel} onClick={(e) => e.stopPropagation()}>
              {/* Gallery - Split Panel with Thumbnail Rail */}
              <div className={styles.modalContent} ref={modalRef}>
                {/* Intro */}
                <div className={styles.introSection}>
                  <div className={styles.introSpread}>
                    <span className={styles.introWatermark}>{selectedService.number}</span>
                    <h2 className={styles.mTitle} data-anim="title">{selectedService.title}</h2>
                    <p className={styles.mDesc} data-anim="desc">{selectedService.description}</p>
                  </div>
                </div>

                {/* Stages - Interactive Timeline */}
                <div className={styles.stagesSection}>
                  <div className={styles.stagesInner}>
                    <h3 className={styles.stagesTitle} data-reveal>שלבי עבודה</h3>
                    <div className={styles.stagesTimeline}>
                      <div className={styles.stagesLine} />
                      {selectedService.stages.map((s, i) => (
                        <div key={s.num}
                          className={styles.stageItem}
                          data-reveal data-reveal-delay={String(i * 0.1)}>
                          <div className={styles.stageIconWrap}>
                            {stageIcons[i]}
                          </div>
                          <h4 className={styles.stageItemTitle}>{s.title}</h4>
                          <p className={styles.stageItemText}>{s.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Image Grid - 4 images */}
                <div className={styles.imageGrid} data-reveal>
                  {selectedService.gallery.map((src, i) => (
                    <div key={i} className={styles.imageGridItem}>
                      <img src={src} alt="" className={styles.imageGridImg} loading="lazy" />
                    </div>
                  ))}
                </div>

                {/* Suitable For */}
                <div className={styles.suitableSection}>
                  <div className={styles.suitableSpread}>
                    <h3 className={styles.suitableTitle} data-reveal>השירות מתאים לכם אם...</h3>
                    <div className={styles.suitableList}>
                      {selectedService.suitableFor.map((item, i) => (
                        <div key={i} className={styles.suitableItem} data-reveal data-reveal-delay={String(i * 0.1)}>
                          <span className={styles.suitableCheck}>
                            <GoCheckCircleFill />
                          </span>
                          <span className={styles.suitableText}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Includes */}
                <div className={styles.includesSection}>
                  <div className={styles.includesInner}>
                    <h3 className={styles.includesTitle} data-reveal>מה כולל השירות</h3>
                    <div className={styles.includesGrid}>
                      {selectedService.includes.map((item, i) => (
                        <div key={i} className={styles.includeCard} data-reveal data-reveal-delay={String(i * 0.06)}>
                          <span className={styles.includeCardDot} />
                          <span className={styles.includeCardText}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className={styles.ctaSection} data-reveal>
                  <div className={styles.ctaInner}>
                    <p className={styles.ctaText}>בואו נוודא שלא תשלמו על טעויות</p>
                    <p className={styles.ctaSubtext}>שיחה קצרה, בלי התחייבות - ואני אגיד לכם בדיוק מה מתאים למצב שלכם</p>
                    <a href={waUrl(selectedService.title)} target="_blank" rel="noopener noreferrer" className={styles.ctaBtn}>
                      <svg className={styles.ctaIcon} viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      <span>לשיחת ייעוץ ללא עלות לחצו כאן</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesGrid;
